import React, { useState } from 'react';
import { Share2, Twitter, Facebook, Linkedin, Link2, Check, MessageCircle } from 'lucide-react';

const ShareButtons = ({ url, title, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);
  
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  
  const shareLinks = {
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setShowShare(!showShare)}
        data-testid="share-button"
        className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
      >
        <Share2 className="w-4 h-4" />
        <span className="text-sm font-medium">Share</span>
      </button>

      {showShare && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[60] bg-slate-950/40 sm:bg-transparent" 
            onClick={() => setShowShare(false)}
          />
          
          {/* Share Menu: bottom sheet on mobile, dropdown on desktop */}
          <div
            data-testid="share-menu"
            className="
              fixed left-1/2 -translate-x-1/2 bottom-4 w-[calc(100vw-2rem)] max-w-sm
              sm:absolute sm:left-auto sm:right-0 sm:translate-x-0 sm:bottom-auto sm:top-full sm:mt-2 sm:w-56
              bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-[70] overflow-hidden
            "
          >
            <div className="p-2">
              <p className="text-xs text-slate-400 px-3 py-2">Share this article</p>
              
              <button
                onClick={() => handleShare('whatsapp')}
                data-testid="share-whatsapp-button"
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <MessageCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm">WhatsApp</span>
              </button>
              
              <button
                onClick={() => handleShare('twitter')}
                data-testid="share-twitter-button"
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Twitter className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Twitter</span>
              </button>
              
              <button
                onClick={() => handleShare('facebook')}
                data-testid="share-facebook-button"
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Facebook className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Facebook</span>
              </button>
              
              <button
                onClick={() => handleShare('linkedin')}
                data-testid="share-linkedin-button"
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Linkedin className="w-4 h-4 text-blue-400" />
                <span className="text-sm">LinkedIn</span>
              </button>
              
              <div className="border-t border-slate-700 mt-1 pt-1">
                <button
                  onClick={copyToClipboard}
                  data-testid="share-copy-link-button"
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400">Link Copied!</span>
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">Copy Link</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShareButtons;
